(() => {
  document.querySelectorAll('.school').forEach(root => {
    if (root.dataset.purchaseReady) return;
    root.dataset.purchaseReady = 'true';
    const config = JSON.parse(root.querySelector('[data-school-config]').textContent);
    const route = config.root.endsWith('/') ? config.root : config.root + '/';
    const buttons = [...root.querySelectorAll('[data-school-buy]')];
    const feedback = root.querySelector('[data-school-feedback]');
    const status = root.querySelector('[data-school-status]');
    const retry = root.querySelector('[data-school-retry]');
    const sticky = root.querySelector('.stickybar');
    const storageKey = 'nurturebio-school-purchase';
    const originalLabels = new Map(buttons.map(button => [button, button.textContent]));
    let busy = false;
    let attempt = null;
    try { attempt = JSON.parse(sessionStorage.getItem(storageKey)); } catch (_) {}
    const save = () => {
      try { sessionStorage.setItem(storageKey, JSON.stringify(attempt)); } catch (_) {}
    };
    const money = cents => new Intl.NumberFormat('en-US', {style: 'currency', currency: config.currency}).format(cents / 100);
    const track = (event, detail) => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({event, campaign: 'school', ...detail});
    };
    const findItem = cart => attempt?.id ? cart.items.find(item => item.variant_id === config.variantId && !item.selling_plan_allocation && item.properties?._school_attempt === attempt.id) : undefined;
    async function request(path, data) {
      const response = await fetch(route + path, {
        credentials: 'same-origin', cache: 'no-store',
        ...(data ? {method: 'POST', headers: {'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest'}, body: JSON.stringify(data)} : {})
      });
      const result = await response.json();
      if (!response.ok) {
        const error = new Error(result.description || result.message || 'Please try again.');
        error.confirmedFailure = true;
        throw error;
      }
      return result;
    }
    function show(message, canRetry = false) {
      status.textContent = message;
      feedback.hidden = false;
      retry.hidden = !canRetry;
      updateSticky();
    }
    function setBusy(value, label) {
      busy = value;
      buttons.forEach(button => {
        button.disabled = value || !config.available;
        button.setAttribute('aria-busy', String(value));
        button.textContent = label || (attempt?.added ? 'Continue to checkout' : originalLabels.get(button));
      });
      retry.disabled = value;
    }
    function announceAdd(cart, item, source) {
      if (attempt.tracked) return;
      attempt.tracked = true;
      save();
      const unitPrice = item.final_price / 100;
      track('add_to_cart', {
        plan: 'onetime', quantity: 1, product_id: config.productId, variant_id: config.variantId,
        ecommerce: {currency: cart.currency, value: unitPrice, items: [{item_id: String(config.productId), item_variant: String(config.variantId), item_name: config.productTitle, price: unitPrice, quantity: 1}]}
      });
      source.dispatchEvent(new CustomEvent('variant:add', {bubbles: true, detail: {items: [{...item, quantity: 1}], cart}}));
    }
    async function purchase(source) {
      if (busy || !config.available) return;
      setBusy(true, 'Checking your cart…');
      feedback.hidden = true;
      track('hero_cta_click', {location: source.dataset.location || source.closest('form')?.dataset.location, plan: 'onetime'});
      let cart;
      try {
        cart = await request('cart.js');
        let item = findItem(cart);
        if (!item) {
          const product = await request('products/' + config.productHandle + '.js');
          const variant = product.variants.find(candidate => candidate.id === config.variantId);
          if (!variant?.available) throw new Error('This 30-day supply is currently sold out.');
          if (variant.requires_selling_plan || variant.price !== config.basePrice || cart.currency !== config.currency) {
            throw new Error('The current product price has changed. Please reload this page before purchasing.');
          }
          attempt = {id: crypto.randomUUID(), added: false, tracked: false};
          save();
          setBusy(true, 'Adding your supply…');
          try {
            const added = await request('cart/add.js', {items: [{id: config.variantId, quantity: 1, properties: {_school_attempt: attempt.id, _campaign: config.code}}]});
            item = added.items[0];
            attempt.added = true;
            save();
          } catch (error) {
            if (error.confirmedFailure) {
              attempt = null;
              save();
              throw error;
            }
            cart = await request('cart.js');
            item = findItem(cart);
            if (!item) throw new Error('We could not confirm the cart update. Please retry; we will check your cart before adding anything.');
            attempt.added = true;
            save();
          }
          cart = await request('cart.js');
          item = findItem(cart);
        } else {
          attempt.added = true;
          save();
        }
        setBusy(true, 'Applying ' + config.code + '…');
        const sections = new Set();
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {bubbles: true, detail: {sections}}));
        const existing = (cart.discount_codes || []).map(discount => discount.code);
        const codes = [...new Set([...existing.map(code => code.toUpperCase()), config.code.toUpperCase()])];
        try {
          cart = await request('cart/update.js', {discount: codes.join(','), sections: [...sections], sections_url: location.pathname});
        } catch (_) {
          announceAdd(cart, item, source);
          throw new Error('Your supply is in your cart, but we could not apply ' + config.code + '. Retry the discount without adding another box.');
        }
        item = findItem(cart);
        if (item) announceAdd(cart, item, source);
        const applicable = cart.discount_codes?.some(discount => discount.code.toUpperCase() === config.code.toUpperCase() && discount.applicable);
        const allocation = item?.line_level_discount_allocations?.some(discount => discount.discount_application.title.toUpperCase() === config.code.toUpperCase());
        if (!item || item.selling_plan_allocation || !applicable || !allocation || item.final_price !== config.price) {
          throw new Error('Your supply is in your cart, but ' + config.code + ' did not produce the ' + money(config.price) + ' offer price. It is valid once per customer and cannot combine with other discounts. Review your cart or retry the discount; another box will not be added.');
        }
        if ([...sections].every(id => cart.sections?.[id])) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {bubbles: true, detail: {baseEvent: 'school:discount', cart}}));
        } else {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {bubbles: true}));
        }
        attempt.added = true;
        save();
        show(config.code + ' applied. Your one-time supply is ' + money(item.final_price) + ' before shipping and taxes. Opening checkout…');
        setBusy(true, 'Opening checkout…');
        window.location.assign(route + 'checkout');
      } catch (error) {
        if (cart && attempt?.added) {
          const item = findItem(cart);
          if (item) announceAdd(cart, item, source);
        }
        show(error.message || 'We could not complete this request. Please retry.', Boolean(attempt?.added));
        retry.textContent = 'Retry discount';
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {bubbles: true}));
        setBusy(false);
      }
    }
    root.querySelectorAll('[data-school-form]').forEach(form => form.addEventListener('submit', event => {
      event.preventDefault(); purchase(event.submitter || form.querySelector('[data-school-buy]'));
    }));
    sticky.querySelector('button').addEventListener('click', event => purchase(event.currentTarget));
    retry.addEventListener('click', event => purchase(event.currentTarget));
    root.querySelector('.school-feedback-close').addEventListener('click', () => {feedback.hidden = true; updateSticky();});
    function updateSticky() {
      const inView = [...root.querySelectorAll('[data-school-form] .buy-cta')].some(button => {
        const rect = button.getBoundingClientRect();
        return rect.top >= 0 && rect.bottom <= window.innerHeight;
      });
      const overlay = [...document.querySelectorAll('cart-drawer[open], dialog[open], [aria-modal="true"], #shopify-pc__banner, #shopify-pc__prefs')].some(element => {
        const rect = element.getBoundingClientRect();
        const style = getComputedStyle(element);
        return element.hasAttribute('open') || (rect.width > 0 && rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden' && element.getAttribute('aria-hidden') !== 'true');
      });
      const visible = window.innerWidth <= 760 && !inView && !overlay && feedback.hidden;
      sticky.hidden = !visible;
      root.toggleAttribute('data-sticky-visible', visible);
      const chat = document.querySelector('shopify-chat')?.shadowRoot?.querySelector('[part="activator"]');
      if (chat) {
        const chatRect = chat.getBoundingClientRect();
        const overlap = [...root.querySelectorAll('form.buybox, .stickybar:not([hidden]), [data-school-feedback]:not([hidden])')].some(element => {
          const rect = element.getBoundingClientRect();
          return rect.left < chatRect.right && rect.right > chatRect.left && rect.top < chatRect.bottom && rect.bottom > chatRect.top;
        });
        root.toggleAttribute('data-chat-obstructed', overlap);
      }
    }
    window.addEventListener('scroll', updateSticky, {passive: true});
    window.addEventListener('resize', updateSticky, {passive: true});
    new MutationObserver(updateSticky).observe(document.body, {subtree: true, attributes: true, attributeFilter: ['open', 'aria-modal', 'style', 'class'], childList: true});
    window.addEventListener('pageshow', async () => {
      setBusy(false);
      if (attempt) {
        try {
          const cart = await request('cart.js');
          if (busy) return;
          if (!findItem(cart)) { attempt = null; save(); }
          else attempt.added = true;
          setBusy(false);
        } catch (_) {}
      }
      updateSticky();
    });
    setBusy(false);
    updateSticky();
  });
})();
